import { FruitCell } from "./FruitCellV2";
import { Card } from "@/components/ui/card";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Sword } from "lucide-react";
import { THEME_CONFIGS } from "@/hooks/useCosmetics";

// Empty set/array constants to avoid creating new objects on every render
const EMPTY_SET = new Set();
const EMPTY_ARRAY = [];

// Sword Slash Animation Component
function SwordSlashAnimation({ tier, boardSize }) {
  const sparkleCount = tier === 'diamond' ? 12 : tier === 'golden' ? 8 : tier === 'silver' ? 6 : 4;
  
  const sparkles = useMemo(() => {
    return Array.from({ length: sparkleCount }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 0.3,
      size: 8 + Math.random() * 8,
    }));
  }, [sparkleCount]);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-50" data-testid="sword-slash-animation">
      {/* Screen flash */}
      <div className="sword-flash-overlay" />
      
      {/* First diagonal slash */}
      <div 
        className={`sword-slash sword-slash-${tier}`}
        style={{ 
          top: '50%', 
          left: '50%',
          transform: 'translate(-50%, -50%) rotate(-45deg)',
        }} 
      />
      
      {/* Second diagonal slash (X pattern) */}
      <div 
        className={`sword-slash sword-slash-2 sword-slash-${tier}`}
        style={{ 
          top: '50%', 
          left: '50%',
          transform: 'translate(-50%, -50%) rotate(45deg)',
        }} 
      />
      
      {/* Shockwave */}
      <div 
        className="sword-shockwave"
        style={{
          top: '50%',
          left: '50%',
          width: `${boardSize * 15}px`,
          height: `${boardSize * 15}px`,
          transform: 'translate(-50%, -50%)',
          borderColor: tier === 'diamond' ? 'rgba(0, 255, 255, 0.6)' : 
                       tier === 'golden' ? 'rgba(255, 215, 0, 0.6)' :
                       tier === 'silver' ? 'rgba(192, 192, 192, 0.6)' :
                       'rgba(205, 127, 50, 0.6)',
        }}
      />
      
      {/* Impact circle */}
      <div 
        className="sword-impact"
        style={{
          top: '50%',
          left: '50%',
          width: `${boardSize * 20}px`,
          height: `${boardSize * 20}px`,
          transform: 'translate(-50%, -50%)',
          background: `radial-gradient(circle, 
            ${tier === 'diamond' ? 'rgba(0, 255, 255, 0.9)' : 
              tier === 'golden' ? 'rgba(255, 215, 0, 0.9)' :
              tier === 'silver' ? 'rgba(192, 192, 192, 0.9)' :
              'rgba(205, 127, 50, 0.9)'} 0%,
            ${tier === 'diamond' ? 'rgba(0, 255, 255, 0.4)' : 
              tier === 'golden' ? 'rgba(255, 215, 0, 0.4)' :
              tier === 'silver' ? 'rgba(192, 192, 192, 0.4)' :
              'rgba(205, 127, 50, 0.4)'} 50%,
            transparent 100%
          )`,
        }}
      />
      
      {/* Sparkles */}
      {sparkles.map(sparkle => (
        <div
          key={sparkle.id}
          className="sword-sparkle"
          style={{
            left: `${sparkle.x}%`,
            top: `${sparkle.y}%`,
            width: `${sparkle.size}px`,
            height: `${sparkle.size}px`,
            animationDelay: `${sparkle.delay}s`,
            background: tier === 'diamond' ? 'cyan' : 
                       tier === 'golden' ? 'gold' :
                       tier === 'silver' ? 'silver' :
                       '#CD7F32',
          }}
        />
      ))}
    </div>
  );
}

// Special Combo Animation Component (Striped + Striped, Striped + Wrapped)
function SpecialComboAnimation({ animation, cellSize, boardSize }) {
  if (!animation) return null;
  
  const { type, row, col, phase } = animation;
  
  // Calculate center position
  const centerX = col * cellSize + cellSize / 2;
  const centerY = row * cellSize + cellSize / 2;
  const totalSize = boardSize * cellSize;
  
  if (type === 'cross') {
    // Cross pattern for Striped + Striped
    return (
      <div className="absolute inset-0 pointer-events-none z-50">
        {/* Horizontal sweep */}
        <div 
          className="absolute bg-gradient-to-r from-transparent via-yellow-400 to-transparent animate-sweep-horizontal"
          style={{
            top: `${row * cellSize}px`,
            left: 0,
            width: '100%',
            height: `${cellSize}px`,
            opacity: 0.8,
          }}
        />
        {/* Vertical sweep */}
        <div 
          className="absolute bg-gradient-to-b from-transparent via-yellow-400 to-transparent animate-sweep-vertical"
          style={{
            top: 0,
            left: `${col * cellSize}px`,
            width: `${cellSize}px`,
            height: '100%',
            opacity: 0.8,
          }}
        />
        {/* Center explosion */}
        <div 
          className="absolute rounded-full bg-white animate-ping"
          style={{
            top: `${centerY - 20}px`,
            left: `${centerX - 20}px`,
            width: '40px',
            height: '40px',
          }}
        />
      </div>
    );
  }
  
  if (type === 'mega') {
    // Mega combo for Striped + Wrapped (3x3 expansion + 3 rows/cols)
    const expandSize = phase === 'expand' ? cellSize * 3 : cellSize * 3;
    
    return (
      <div className="absolute inset-0 pointer-events-none z-50">
        {/* 3x3 expanding center */}
        {phase === 'expand' && (
          <div 
            className="absolute rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 animate-mega-expand"
            style={{
              top: `${(row - 1) * cellSize}px`,
              left: `${(col - 1) * cellSize}px`,
              width: `${cellSize * 3}px`,
              height: `${cellSize * 3}px`,
              transformOrigin: 'center center',
            }}
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-6xl animate-bounce">💥</span>
            </div>
          </div>
        )}
        
        {/* Sweep effect for 3 rows */}
        {phase === 'sweep' && (
          <>
            {[-1, 0, 1].map(offset => (
              <div 
                key={`row-${offset}`}
                className="absolute bg-gradient-to-r from-purple-500/0 via-purple-500/80 to-purple-500/0 animate-sweep-horizontal-fast"
                style={{
                  top: `${(row + offset) * cellSize}px`,
                  left: 0,
                  width: '100%',
                  height: `${cellSize}px`,
                  animationDelay: `${Math.abs(offset) * 0.1}s`,
                }}
              />
            ))}
            {/* Sweep effect for 3 columns */}
            {[-1, 0, 1].map(offset => (
              <div 
                key={`col-${offset}`}
                className="absolute bg-gradient-to-b from-pink-500/0 via-pink-500/80 to-pink-500/0 animate-sweep-vertical-fast"
                style={{
                  top: 0,
                  left: `${(col + offset) * cellSize}px`,
                  width: `${cellSize}px`,
                  height: '100%',
                  animationDelay: `${Math.abs(offset) * 0.1}s`,
                }}
              />
            ))}
          </>
        )}
        
        {/* Sparkle particles */}
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            key={i}
            className="absolute animate-sparkle-fly"
            style={{
              top: `${centerY}px`,
              left: `${centerX}px`,
              transform: `rotate(${i * 30}deg) translateY(-${20 + Math.random() * 40}px)`,
              animationDelay: `${Math.random() * 0.3}s`,
            }}
          >
            ✨
          </div>
        ))}
      </div>
    );
  }
  
  // NEW: Mega Explosion for Wrapped + Wrapped combo (4x4 explosion)
  if (type === 'mega_explosion') {
    return (
      <div className="absolute inset-0 pointer-events-none z-50">
        {/* Central explosion effect */}
        <div 
          className="absolute rounded-full"
          style={{
            top: `${(row - 2) * cellSize}px`,
            left: `${(col - 2) * cellSize}px`,
            width: `${cellSize * 5}px`,
            height: `${cellSize * 5}px`,
            transformOrigin: 'center center',
          }}
        >
          {/* Wave 1 - Pink inner explosion */}
          {(phase === 'wave1' || phase === 'wave2') && (
            <div 
              className="absolute inset-0 rounded-full animate-mega-explosion-wave1"
              style={{
                background: 'radial-gradient(circle, rgba(255, 105, 180, 0.9) 0%, rgba(255, 20, 147, 0.7) 40%, transparent 70%)',
              }}
            />
          )}
          
          {/* Wave 2 - Golden outer explosion */}
          {phase === 'wave2' && (
            <div 
              className="absolute inset-0 rounded-full animate-mega-explosion-wave2"
              style={{
                background: 'radial-gradient(circle, rgba(255, 215, 0, 0.9) 0%, rgba(255, 140, 0, 0.7) 40%, transparent 70%)',
                animationDelay: '0.15s',
              }}
            />
          )}
          
          {/* Center icon */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-7xl animate-bounce" style={{ animationDuration: '0.3s' }}>💥</span>
          </div>
        </div>
        
        {/* Sparkle ring */}
        {Array.from({ length: 16 }).map((_, i) => (
          <div
            key={i}
            className="absolute animate-sparkle-fly"
            style={{
              top: `${centerY}px`,
              left: `${centerX}px`,
              transform: `rotate(${i * 22.5}deg) translateY(-${40 + Math.random() * 60}px)`,
              animationDelay: `${Math.random() * 0.2}s`,
              fontSize: '1.5rem',
            }}
          >
            {['✨', '💫', '⭐', '🌟'][i % 4]}
          </div>
        ))}
      </div>
    );
  }
  
  // NEW: Disco + Striped combo animation
  if (type === 'disco_striped') {
    const { positions = [], targetType } = animation;
    
    return (
      <div className="absolute inset-0 pointer-events-none z-50">
        {/* Rainbow background flash */}
        {phase === 'rainbow' && (
          <div 
            className="absolute inset-0 animate-disco-striped-rainbow"
            style={{
              background: 'linear-gradient(45deg, #ff0000, #ff8800, #ffff00, #00ff00, #0088ff, #8800ff)',
              opacity: 0.3,
            }}
          />
        )}
        
        {/* Transform markers at each target fruit position */}
        {phase === 'transform' && positions.map((pos, i) => (
          <div
            key={i}
            className="absolute animate-disco-striped-transform"
            style={{
              top: `${pos.row * cellSize}px`,
              left: `${pos.col * cellSize}px`,
              width: `${cellSize}px`,
              height: `${cellSize}px`,
              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.8), rgba(255, 100, 0, 0.6))',
              borderRadius: '8px',
              animationDelay: `${i * 0.05}s`,
            }}
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl">⚡</span>
            </div>
          </div>
        ))}
        
        {/* Activation sweeps */}
        {phase === 'activate' && positions.map((pos, i) => (
          <div key={`sweep-${i}`}>
            {/* Horizontal sweep */}
            <div 
              className="absolute bg-gradient-to-r from-transparent via-yellow-400 to-transparent animate-sweep-horizontal-fast"
              style={{
                top: `${pos.row * cellSize}px`,
                left: 0,
                width: '100%',
                height: `${cellSize}px`,
                opacity: 0.7,
                animationDelay: `${i * 0.05}s`,
              }}
            />
            {/* Vertical sweep */}
            <div 
              className="absolute bg-gradient-to-b from-transparent via-orange-400 to-transparent animate-sweep-vertical-fast"
              style={{
                top: 0,
                left: `${pos.col * cellSize}px`,
                width: `${cellSize}px`,
                height: '100%',
                opacity: 0.7,
                animationDelay: `${i * 0.05}s`,
              }}
            />
          </div>
        ))}
        
        {/* Disco ball particles */}
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            className="absolute animate-sparkle-fly"
            style={{
              top: `${centerY}px`,
              left: `${centerX}px`,
              transform: `rotate(${i * 18}deg) translateY(-${30 + Math.random() * 80}px)`,
              animationDelay: `${Math.random() * 0.4}s`,
              fontSize: '1.2rem',
            }}
          >
            {['🌈', '✨', '⚡', '💫', '🪩'][i % 5]}
          </div>
        ))}
      </div>
    );
  }
  
  return null;
}

export function GameBoard({ 
  board, 
  selectedCell, 
  onCellClick, 
  onSwipe,
  isAnimating,
  explodingCells = EMPTY_SET,
  clearEffect = null,
  boardSize = 8,
  swappingCells = null,
  swordMode = false,
  swordTier = null,
  onSwordUse = null,
  swordSlashAnimation = null,
  specialComboAnimation = null,
  bombMode = false,
  colorBombMode = false,
  wrappedMode = false,
  discoMode = false,
  stripedMode = false,
  hintCell = null,
  hintCells = EMPTY_ARRAY,  // Advanced hint system: array of hint cells
  equippedSkin = null, // Cosmetic skin from useCosmetics
  equippedEffect = null, // Cosmetic effect from useCosmetics
  equippedTheme = null, // Board theme from useCosmetics
  // God Sword
  godSwordMode = false,
  godSwordAnimation = false,
  onGodSwordUse = null,
  // World mechanics
  lavaLevel = 0,
  worldMechanic = null,
  comboCount = 0,
}) {
  const [boardShake, setBoardShake] = useState(false);
  const [localNewCells, setLocalNewCells] = useState(new Set());
  const prevBoardRef = useRef(null);
  const [swordHoverCell, setSwordHoverCell] = useState(null);
  
  // Swipe tracking
  const [touchStart, setTouchStart] = useState(null);
  const [touchCell, setTouchCell] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Detect new cells by comparing with previous board
  useEffect(() => {
    if (prevBoardRef.current && explodingCells.size === 0) {
      const newCellIds = new Set();
      board.forEach((row, rowIndex) => {
        row.forEach((cell, colIndex) => {
          if (cell) {
            const prevCell = prevBoardRef.current[rowIndex]?.[colIndex];
            if (!prevCell || prevCell.id !== cell.id) {
              newCellIds.add(`${rowIndex}-${colIndex}`);
            }
          }
        });
      });
      if (newCellIds.size > 0) {
        setLocalNewCells(newCellIds);
        setTimeout(() => setLocalNewCells(new Set()), 500);
        
        if (newCellIds.size > 10) {
          setBoardShake(true);
          setTimeout(() => setBoardShake(false), 500);
        }
      }
    }
    prevBoardRef.current = board.map(row => row.map(cell => cell ? { ...cell } : null));
  }, [board, explodingCells.size]);

  // Shake on big explosions
  useEffect(() => {
    if (explodingCells.size > 8) {
      setBoardShake(true);
      setTimeout(() => setBoardShake(false), 400);
    }
  }, [explodingCells.size]);

  // Handle touch/mouse start
  const handlePointerDown = useCallback((e, row, col) => {
    if (isAnimating) return;
    
    // If God Sword mode is active, use God Sword ability (clears entire board)
    if (godSwordMode && onGodSwordUse) {
      onGodSwordUse();
      return;
    }
    
    // If sword mode is active, use sword ability
    if (swordMode && onSwordUse) {
      onSwordUse(row, col);
      return;
    }
    
    // Prevent default to stop page scrolling on touch devices
    if (e.cancelable) {
      e.preventDefault();
    }
    
    const point = e.touches ? e.touches[0] : e;
    setTouchStart({ x: point.clientX, y: point.clientY });
    setTouchCell({ row, col });
    setIsDragging(true);
  }, [isAnimating, swordMode, onSwordUse, godSwordMode, onGodSwordUse]);

  // Handle mouse enter for sword hover effect
  const handleCellHover = useCallback((row, col) => {
    if (swordMode) {
      setSwordHoverCell({ row, col });
    }
  }, [swordMode]);

  // Clear sword hover when leaving board
  const handleBoardLeave = useCallback(() => {
    setSwordHoverCell(null);
  }, []);

  // Check if cell is in sword hover range (NxN around hover cell based on sword tier)
  const isInSwordRange = useCallback((row, col) => {
    if (!swordHoverCell || !swordMode || !swordTier) return false;
    const { row: hoverRow, col: hoverCol } = swordHoverCell;
    
    // Calculate range based on sword size
    const swordSize = swordTier.size;
    const halfSize = Math.floor(swordSize / 2);
    
    // For even sizes, the range is slightly asymmetric
    const rowInRange = row >= hoverRow - halfSize && row <= hoverRow + halfSize + (swordSize % 2 === 0 ? 0 : 0);
    const colInRange = col >= hoverCol - halfSize && col <= hoverCol + halfSize + (swordSize % 2 === 0 ? 0 : 0);
    
    return rowInRange && colInRange;
  }, [swordHoverCell, swordMode, swordTier]);

  // Handle touch/mouse move (for visual feedback)
  const handlePointerMove = useCallback((e) => {
    if (!isDragging || !touchStart || !touchCell) return;
    
    // Prevent default to stop page scrolling during swipe
    if (e.cancelable) {
      e.preventDefault();
    }
  }, [isDragging, touchStart, touchCell]);

  // Handle touch/mouse end - detect swipe direction
  const handlePointerUp = useCallback((e) => {
    if (!touchStart || !touchCell || isAnimating) {
      setTouchStart(null);
      setTouchCell(null);
      setIsDragging(false);
      return;
    }

    const point = e.changedTouches ? e.changedTouches[0] : e;
    const deltaX = point.clientX - touchStart.x;
    const deltaY = point.clientY - touchStart.y;
    
    const minSwipeDistance = 30; // Minimum pixels to register as swipe
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    // Check if it's a swipe (not just a tap)
    if (absX > minSwipeDistance || absY > minSwipeDistance) {
      let direction = null;
      
      // Determine swipe direction
      if (absX > absY) {
        // Horizontal swipe
        direction = deltaX > 0 ? 'right' : 'left';
      } else {
        // Vertical swipe
        direction = deltaY > 0 ? 'down' : 'up';
      }

      // Calculate target cell based on direction
      let targetRow = touchCell.row;
      let targetCol = touchCell.col;

      switch (direction) {
        case 'up':
          targetRow = touchCell.row - 1;
          break;
        case 'down':
          targetRow = touchCell.row + 1;
          break;
        case 'left':
          targetCol = touchCell.col - 1;
          break;
        case 'right':
          targetCol = touchCell.col + 1;
          break;
      }

      // Check if target is within bounds
      if (targetRow >= 0 && targetRow < boardSize && 
          targetCol >= 0 && targetCol < boardSize) {
        // Trigger swipe callback
        if (onSwipe) {
          onSwipe(touchCell.row, touchCell.col, targetRow, targetCol, direction);
        }
      }
    } else {
      // It's a tap, use regular click handler
      onCellClick(touchCell.row, touchCell.col);
    }

    setTouchStart(null);
    setTouchCell(null);
    setIsDragging(false);
  }, [touchStart, touchCell, isAnimating, boardSize, onSwipe, onCellClick]);

  // Global pointer up handler and touch move prevention
  useEffect(() => {
    const handleGlobalPointerUp = (e) => {
      if (isDragging) {
        handlePointerUp(e);
      }
    };
    
    // Prevent scrolling when dragging on the game board
    const handleGlobalTouchMove = (e) => {
      if (isDragging && e.cancelable) {
        e.preventDefault();
      }
    };

    window.addEventListener('mouseup', handleGlobalPointerUp);
    window.addEventListener('touchend', handleGlobalPointerUp);
    // Use passive: false to allow preventDefault on touchmove
    window.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });

    return () => {
      window.removeEventListener('mouseup', handleGlobalPointerUp);
      window.removeEventListener('touchend', handleGlobalPointerUp);
      window.removeEventListener('touchmove', handleGlobalTouchMove);
    };
  }, [isDragging, handlePointerUp]);

  // Calculate cell percentage based on board size
  const cellPercentage = 100 / boardSize;
  // Reduce max board size for mobile - adjust for different board sizes
  const maxBoardSize = boardSize === 12 ? '480px' : boardSize === 10 ? '420px' : '360px';

  // Get swap animation style for a cell
  const getSwapStyle = (row, col) => {
    if (!swappingCells) return {};
    
    const { from, to, direction } = swappingCells;
    const cellSize = boardSize === 12 ? 38 : boardSize === 10 ? 45 : 48; // Approximate cell size in pixels
    
    if (from.row === row && from.col === col) {
      // This is the source cell - animate towards target
      const translateX = (to.col - from.col) * cellSize;
      const translateY = (to.row - from.row) * cellSize;
      return {
        transform: `translate(${translateX}px, ${translateY}px)`,
        transition: 'transform 0.2s ease-out',
        zIndex: 20,
      };
    }
    
    if (to.row === row && to.col === col) {
      // This is the target cell - animate towards source
      const translateX = (from.col - to.col) * cellSize;
      const translateY = (from.row - to.row) * cellSize;
      return {
        transform: `translate(${translateX}px, ${translateY}px)`,
        transition: 'transform 0.2s ease-out',
        zIndex: 19,
      };
    }
    
    return {};
  };

  // Get theme configuration
  const themeConfig = equippedTheme ? THEME_CONFIGS[equippedTheme] : null;

  return (
    <Card 
      className={`p-3 sm:p-4 bg-game-board shadow-playful border-2 border-border rounded-2xl relative overflow-hidden ${boardShake ? 'board-shake' : ''} ${swordMode ? `ring-4 ${swordTier?.name === 'Diamond' ? 'ring-cyan-400' : swordTier?.name === 'Golden' ? 'ring-yellow-400' : swordTier?.name === 'Silver' ? 'ring-gray-400' : 'ring-amber-400'} ring-opacity-70` : ''} ${godSwordMode ? 'ring-4 ring-yellow-400 ring-opacity-90' : ''} ${themeConfig?.cssClass || ''}`}
      style={{ 
        touchAction: 'none',
        ...(themeConfig ? {
          background: themeConfig.boardBackground,
          borderColor: themeConfig.borderColor,
        } : {})
      }}
      onMouseMove={handlePointerMove}
      onTouchMove={handlePointerMove}
      onMouseLeave={handleBoardLeave}
    >
      {/* God Sword mode indicator */}
      {godSwordMode && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-30 bg-gradient-to-r from-yellow-500 to-amber-600 text-white px-4 py-1.5 rounded-full text-sm font-bold flex items-center gap-2 animate-pulse shadow-lg">
          <span className="text-lg">⚔️</span>
          Tap anywhere to CLEAR ALL!
        </div>
      )}
      
      {/* God Sword animation overlay - Epic slash effect */}
      {godSwordAnimation && (
        <div className="absolute inset-0 z-40 pointer-events-none overflow-hidden">
          {/* Background flash */}
          <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/60 via-amber-500/40 to-red-500/60 animate-pulse" />
          
          {/* Diagonal slash lines */}
          <div className="absolute inset-0">
            {/* First slash - top-left to bottom-right */}
            <div 
              className="absolute w-[200%] h-1.5 bg-gradient-to-r from-transparent via-white to-transparent"
              style={{
                top: '50%',
                left: '-50%',
                transform: 'rotate(45deg)',
                animation: 'god-sword-slash 0.4s ease-out forwards',
                boxShadow: '0 0 20px 8px rgba(255, 255, 255, 0.8), 0 0 40px 15px rgba(255, 200, 0, 0.6)'
              }}
            />
            {/* Second slash - top-right to bottom-left */}
            <div 
              className="absolute w-[200%] h-1.5 bg-gradient-to-r from-transparent via-white to-transparent"
              style={{
                top: '50%',
                left: '-50%',
                transform: 'rotate(-45deg)',
                animation: 'god-sword-slash 0.4s ease-out 0.15s forwards',
                boxShadow: '0 0 20px 8px rgba(255, 255, 255, 0.8), 0 0 40px 15px rgba(255, 200, 0, 0.6)',
                opacity: 0
              }}
            />
            {/* Horizontal slash */}
            <div 
              className="absolute w-[150%] h-1 bg-gradient-to-r from-transparent via-yellow-300 to-transparent"
              style={{
                top: '50%',
                left: '-25%',
                animation: 'god-sword-slash 0.3s ease-out 0.3s forwards',
                boxShadow: '0 0 15px 5px rgba(255, 200, 0, 0.8)',
                opacity: 0
              }}
            />
          </div>
          
          {/* Center sword icon with spin */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div 
              className="text-7xl"
              style={{
                animation: 'god-sword-spin 0.6s ease-out forwards',
                textShadow: '0 0 30px rgba(255, 200, 0, 1), 0 0 60px rgba(255, 150, 0, 0.8)'
              }}
            >
              ⚔️
            </div>
          </div>
          
          {/* Impact sparks */}
          <div className="absolute inset-0 flex items-center justify-center">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2 h-2 bg-yellow-300 rounded-full"
                style={{
                  animation: `god-sword-spark 0.5s ease-out ${0.2 + i * 0.05}s forwards`,
                  transform: `rotate(${i * 45}deg) translateY(-60px)`,
                  opacity: 0
                }}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* God Sword CSS Animations */}
      <style>{`
        @keyframes god-sword-slash {
          0% { opacity: 0; transform: rotate(inherit) scaleX(0); }
          50% { opacity: 1; }
          100% { opacity: 0; transform: rotate(inherit) scaleX(1); }
        }
        @keyframes god-sword-spin {
          0% { transform: scale(0.5) rotate(-180deg); opacity: 0; }
          50% { transform: scale(1.3) rotate(0deg); opacity: 1; }
          100% { transform: scale(1) rotate(15deg); opacity: 0.8; }
        }
        @keyframes god-sword-spark {
          0% { opacity: 0; transform: rotate(inherit) translateY(0); }
          50% { opacity: 1; }
          100% { opacity: 0; transform: rotate(inherit) translateY(-100px); }
        }
      `}</style>
      
      {/* Sword mode indicator */}
      {swordMode && swordTier && (
        <div className={`absolute top-2 left-1/2 -translate-x-1/2 z-30 ${swordTier.name === 'Diamond' ? 'bg-cyan-600' : swordTier.name === 'Golden' ? 'bg-yellow-600' : swordTier.name === 'Silver' ? 'bg-gray-500' : 'bg-amber-600'} text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 animate-pulse`}>
          <Sword className="w-3 h-3" />
          Tap to cut {swordTier.size}x{swordTier.size}!
        </div>
      )}
      
      {/* Row clear effect overlay */}
      {clearEffect?.type === 'row' && (
        <div 
          className="absolute left-3 right-3 z-20 pointer-events-none"
          style={{ 
            top: `calc(${clearEffect.index * cellPercentage}% + 12px)`,
            height: `calc(${cellPercentage}% - 3px)`,
          }}
        >
          <div className="w-full h-full bg-gradient-to-r from-transparent via-accent/60 to-transparent animate-pulse row-clear-effect" />
        </div>
      )}
      
      {/* Column clear effect overlay */}
      {clearEffect?.type === 'column' && (
        <div 
          className="absolute top-3 bottom-3 z-20 pointer-events-none"
          style={{ 
            left: `calc(${clearEffect.index * cellPercentage}% + 12px)`,
            width: `calc(${cellPercentage}% - 3px)`,
          }}
        >
          <div className="w-full h-full bg-gradient-to-b from-transparent via-accent/60 to-transparent animate-pulse column-clear-effect" />
        </div>
      )}

      {/* Sword slash animation */}
      {swordSlashAnimation && (
        <SwordSlashAnimation 
          tier={swordSlashAnimation.tier} 
          boardSize={boardSize}
        />
      )}

      {/* Special combo animation (Striped + Striped, Striped + Wrapped) */}
      {specialComboAnimation && (
        <SpecialComboAnimation 
          animation={specialComboAnimation}
          cellSize={parseInt(maxBoardSize) / boardSize}
          boardSize={boardSize}
        />
      )}

      {/* Lava rise overlay - visual lava creeping up from bottom */}
      {worldMechanic === 'lava_rise' && lavaLevel > 0 && (
        <div className="absolute bottom-0 left-0 right-0 pointer-events-none z-10 overflow-hidden rounded-b-2xl" data-testid="lava-overlay"
          style={{ height: `${Math.min((lavaLevel / boardSize) * 100, 100)}%`, transition: 'height 0.8s ease-in-out' }}>
          <div className="absolute inset-0 bg-gradient-to-t from-red-700/80 via-orange-600/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-orange-500/50 via-yellow-500/20 to-transparent animate-pulse" />
          {/* Lava bubbles */}
          {[...Array(Math.min(lavaLevel * 2, 8))].map((_, i) => (
            <div key={i} className="absolute rounded-full" style={{
              width: `${6 + Math.random() * 10}px`, height: `${6 + Math.random() * 10}px`,
              left: `${10 + (i * 12) % 80}%`, bottom: `${5 + (i * 7) % 30}%`,
              background: 'radial-gradient(circle, #ff6b00 0%, #ff4500 60%, transparent 100%)',
              animation: `lavaBubble ${1.5 + i * 0.3}s ease-in-out infinite ${i * 0.2}s`,
            }} />
          ))}
          {/* Top glowing edge */}
          <div className="absolute top-0 left-0 right-0 h-3 bg-gradient-to-b from-yellow-400/70 to-transparent" style={{ filter: 'blur(4px)' }} />
        </div>
      )}

      {/* World combo reaction overlay */}
      {comboCount >= 4 && worldMechanic && (
        <div className="absolute inset-0 pointer-events-none z-10 overflow-hidden rounded-2xl" data-testid="combo-reaction"
          style={{ animation: 'comboFlash 0.6s ease-out' }}>
          {worldMechanic === 'lava_rise' && (
            <div className="absolute inset-0 bg-gradient-to-b from-red-600/30 via-transparent to-orange-600/30" />
          )}
          {worldMechanic === 'day_night' && (
            <div className="absolute inset-0 bg-gradient-to-b from-purple-500/20 via-transparent to-yellow-400/20" />
          )}
          {worldMechanic === 'gravity_shift' && (
            <div className="absolute inset-0 bg-gradient-to-b from-indigo-500/20 via-transparent to-purple-500/20" />
          )}
          {worldMechanic === 'lightning_strikes' && (
            <div className="absolute inset-0 bg-gradient-to-b from-yellow-400/30 via-transparent to-transparent" />
          )}
          {worldMechanic === 'random_multipliers' && (
            <div className="absolute inset-0 bg-gradient-to-b from-amber-400/20 via-transparent to-transparent" />
          )}
        </div>
      )}

      {/* Lava + combo CSS */}
      <style>{`
        @keyframes lavaBubble {
          0%, 100% { transform: translateY(0) scale(1); opacity: 0.7; }
          50% { transform: translateY(-12px) scale(1.3); opacity: 1; }
        }
        @keyframes comboFlash {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
      `}</style>

      <div 
        className="grid gap-0.5"
        style={{ 
          gridTemplateColumns: `repeat(${boardSize}, 1fr)`,
          width: `min(82vw, ${maxBoardSize})`,
          height: `min(82vw, ${maxBoardSize})`,
        }}
      >
        {board.map((row, rowIndex) =>
          row.map((cell, colIndex) => {
            const isSelected = 
              selectedCell?.row === rowIndex && selectedCell?.col === colIndex;
            
            const isAdjacent = selectedCell && (
              (Math.abs(selectedCell.row - rowIndex) === 1 && selectedCell.col === colIndex) ||
              (Math.abs(selectedCell.col - colIndex) === 1 && selectedCell.row === rowIndex)
            );

            const cellKey = `${rowIndex}-${colIndex}`;
            const isNew = localNewCells.has(cellKey);
            const isExploding = explodingCells.has(cellKey);
            const swapStyle = getSwapStyle(rowIndex, colIndex);
            const isSwapping = swappingCells && (
              (swappingCells.from.row === rowIndex && swappingCells.from.col === colIndex) ||
              (swappingCells.to.row === rowIndex && swappingCells.to.col === colIndex)
            );
            const inSwordRange = isInSwordRange(rowIndex, colIndex);
            const isHinted = hintCell && hintCell.row === rowIndex && hintCell.col === colIndex;
            const isAdvancedHinted = hintCells.some(h => h.row === rowIndex && h.col === colIndex);
            const showBombCursor = bombMode || colorBombMode;
            const showPlacementCursor = wrappedMode || discoMode || stripedMode;

            return (
              <div 
                key={`${rowIndex}-${colIndex}-${cell?.id || 'empty'}`}
                style={swapStyle}
                className={`
                  ${isSwapping ? 'relative' : ''} 
                  ${inSwordRange ? `ring-2 ${swordTier?.name === 'Diamond' ? 'ring-cyan-500' : swordTier?.name === 'Golden' ? 'ring-yellow-500' : swordTier?.name === 'Silver' ? 'ring-gray-400' : 'ring-amber-500'} ring-opacity-80 rounded-lg z-10` : ''} 
                  ${swordMode || showBombCursor || showPlacementCursor ? 'cursor-crosshair' : ''}
                  ${wrappedMode ? 'hover:ring-2 hover:ring-pink-400 hover:rounded-lg' : ''}
                  ${discoMode ? 'hover:ring-2 hover:ring-purple-400 hover:rounded-lg' : ''}
                  ${stripedMode ? 'hover:ring-2 hover:ring-cyan-400 hover:rounded-lg' : ''}
                  ${(isHinted || isAdvancedHinted) ? 'animate-pulse ring-4 ring-yellow-400 rounded-lg z-20 shadow-lg shadow-yellow-400/50' : ''}
                `}
                onMouseDown={(e) => handlePointerDown(e, rowIndex, colIndex)}
                onTouchStart={(e) => handlePointerDown(e, rowIndex, colIndex)}
                onMouseEnter={() => handleCellHover(rowIndex, colIndex)}
              >
                <FruitCell
                  cell={cell}
                  isSelected={isSelected}
                  isAdjacent={isAdjacent && !isSelected}
                  onClick={() => {}} // Click handled by pointer events
                  disabled={isAnimating}
                  isNew={isNew}
                  isExploding={isExploding}
                  small={boardSize >= 10}
                  extraSmall={boardSize >= 12}
                  equippedSkin={equippedSkin}
                  equippedEffect={equippedEffect}
                />
                {/* Sword highlight overlay - subtle so fruit is visible */}
                {inSwordRange && (
                  <div className={`absolute inset-0 ${swordTier?.name === 'Diamond' ? 'bg-cyan-400/20' : swordTier?.name === 'Golden' ? 'bg-yellow-400/20' : swordTier?.name === 'Silver' ? 'bg-gray-400/20' : 'bg-amber-400/20'} rounded-lg pointer-events-none border-2 ${swordTier?.name === 'Diamond' ? 'border-cyan-400' : swordTier?.name === 'Golden' ? 'border-yellow-400' : swordTier?.name === 'Silver' ? 'border-gray-400' : 'border-amber-400'}`} />
                )}
                {/* Bomb mode hover highlight - just border, no fill */}
                {bombMode && cell && !cell.isGap && (
                  <div className="absolute inset-0 rounded-lg pointer-events-none border-2 border-red-500 border-dashed" />
                )}
                {/* Color bomb mode hover highlight - just border, no fill */}
                {colorBombMode && cell && !cell.isGap && (
                  <div className="absolute inset-0 rounded-lg pointer-events-none border-2 border-purple-500 border-dashed" />
                )}
                {/* Placement mode highlight overlays - just borders */}
                {wrappedMode && cell && !cell.isGap && !cell.isIngredient && (
                  <div className="absolute inset-0 rounded-lg pointer-events-none border-2 border-pink-400 border-dashed" />
                )}
                {discoMode && cell && !cell.isGap && !cell.isIngredient && (
                  <div className="absolute inset-0 rounded-lg pointer-events-none border-2 border-purple-400 border-dashed" />
                )}
                {stripedMode && cell && !cell.isGap && !cell.isIngredient && (
                  <div className="absolute inset-0 rounded-lg pointer-events-none border-2 border-cyan-400 border-dashed" />
                )}
                {/* Hint highlight overlay */}
                {isHinted && (
                  <div className="absolute inset-0 bg-yellow-400/40 rounded-lg pointer-events-none animate-pulse" />
                )}
              </div>
            );
          })
        )}
      </div>
      
      {/* Swipe hint overlay when dragging */}
      {isDragging && touchCell && (
        <div className="absolute inset-0 pointer-events-none z-30">
          <div 
            className="absolute w-16 h-16 border-4 border-accent rounded-xl animate-pulse"
            style={{
              left: `calc(${touchCell.col * cellPercentage}% + 8px)`,
              top: `calc(${touchCell.row * cellPercentage}% + 8px)`,
              width: `calc(${cellPercentage}% - 4px)`,
              height: `calc(${cellPercentage}% - 4px)`,
            }}
          />
        </div>
      )}
    </Card>
  );
}
